import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { QUANTITY_RELATIONS } from "./quantityRuleUtils";
import {
  compilePositionRule,
  createCondition,
  parsePositionRule,
  SETTING_MODES,
  createExpression,
  createOperand,
  createPositionRule,
  createVector,
  OPERATORS,
  validatePositionRule
} from "./positionRuleUtils";

function OperandEditor({ operand, settings, onChange, label = "Operandus" }) {
  const setType = (type) =>
    onChange(
      type === "dimension"
        ? createOperand(type, "width")
        : type === "setting"
          ? createOperand(type, settings[0]?.settingId || "")
          : createOperand(type, type === "text" ? "" : "0")
    );

  return (
    <div className="d-flex gap-1 flex-grow-1">
      <Form.Select
        size="sm"
        aria-label={`${label}: Típus`}
        value={operand.type}
        onChange={(event) => setType(event.target.value)}
      >
        <option value="number">Szám</option>
        <option value="dimension">Tervméret</option>
        <option value="setting">Beállítás</option>
        <option value="text">Szöveg</option>
        <option value="expression">Kifejezés</option>
      </Form.Select>
      {["number", "text", "expression"].includes(operand.type) ? (
        <Form.Control
          size="sm"
          aria-label={`${label}: Érték`}
          type={operand.type === "number" ? "number" : "text"}
          value={operand.value}
          onChange={(event) => onChange({ ...operand, value: event.target.value })}
        />
      ) : operand.type === "dimension" ? (
        <Form.Select
          size="sm"
          aria-label={`${label}: Méret`}
          value={operand.value}
          onChange={(event) => onChange({ ...operand, value: event.target.value })}
        >
          <option value="width">Szélesség</option>
          <option value="height">Magasság</option>
          <option value="depth">Mélység</option>
          <option value="qty">Darabszám</option>
        </Form.Select>
      ) : (
        <Form.Select
          size="sm"
          aria-label={`${label}: Beállítás`}
          value={operand.value}
          onChange={(event) => onChange({ ...operand, value: event.target.value })}
        >
          <option value="">Válassz…</option>
          {operand.value && !settings.some((setting) => String(setting.settingId) === String(operand.value)) &&
            <option value={operand.value}>#{operand.value}</option>}
          {settings.map((setting) => (
            <option key={setting.settingId} value={setting.settingId}>
              {setting.name || `#${setting.settingId}`} (#{setting.settingId})
            </option>
          ))}
        </Form.Select>
      )}
      {operand.type === "setting" && <Form.Select size="sm" aria-label={`${label}: Művelet`}
        value={operand.mode || ""} onChange={(event) => onChange({ ...operand, mode: event.target.value })}>
        {SETTING_MODES.map((mode) => <option key={mode} value={mode}>{mode || "Alapérték"}</option>)}
      </Form.Select>}
    </div>
  );
}

function ExpressionEditor({ axis, expression, settings, onChange }) {
  const updateOperation = (index, patch) => {
    const operations = [...expression.operations];
    operations[index] = { ...operations[index], ...patch };
    onChange({ ...expression, operations });
  };

  return (
    <div className="mb-2">
      <div className="d-flex align-items-center gap-1">
        <strong style={{ minWidth: 20, whiteSpace: "nowrap" }}>{axis}</strong>
        <Button size="sm" variant="outline-danger" title="Kifejezés törlése"
          aria-label={`${axis}: Kifejezés törlése`} onClick={() => onChange(createExpression())}>×</Button>
        <Form.Check
          type="checkbox"
          label="−"
          checked={expression.negative}
          onChange={(event) =>
            onChange({ ...expression, negative: event.target.checked })
          }
        />
        <OperandEditor
          label={axis}
          operand={expression.first}
          settings={settings}
          onChange={(first) => onChange({ ...expression, first })}
        />
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() =>
            onChange({
              ...expression,
              operations: [
                ...expression.operations,
                { operator: "+", operand: createOperand() }
              ]
            })
          }
        >
          + művelet
        </Button>
      </div>
      {expression.operations.map((operation, index) => (
        <div className="d-flex gap-1 mt-1 ms-4" key={index}>
          <Form.Select
            size="sm"
            style={{ width: 64 }}
            value={operation.operator}
            onChange={(event) =>
              updateOperation(index, { operator: event.target.value })
            }
          >
            {OPERATORS.map((operator) => (
              <option key={operator}>{operator}</option>
            ))}
          </Form.Select>
          <OperandEditor
            label={`${axis} ${index + 1}`}
            operand={operation.operand}
            settings={settings}
            onChange={(operand) => updateOperation(index, { operand })}
          />
          <Button
            size="sm"
            variant="outline-danger"
            onClick={() =>
              onChange({
                ...expression,
                operations: expression.operations.filter((_, i) => i !== index)
              })
            }
          >
            ×
          </Button>
        </div>
      ))}
    </div>
  );
}

function VectorListEditor({ title, vectors, settings, onChange, kind }) {
  const updateVector = (index, axis, expression) => {
    const next = [...vectors];
    next[index] = { ...next[index], [axis]: expression };
    onChange(next);
  };

  return (
    <div className="mb-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <strong>{title}</strong>
        <Button
          size="sm"
          variant="outline-primary"
          onClick={() => onChange([...vectors, createVector()])}
        >
          + {kind}
        </Button>
      </div>
      {vectors.map((vector, index) => (
        <Card className="mb-2" key={index}>
          <Card.Body className="py-2">
            <div className="d-flex justify-content-between mb-2">
              <span>{kind} {index + 1}</span>
              {(
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => onChange(vectors.length > 1 ? vectors.filter((_, i) => i !== index) : [createVector()])}
                >
                  Törlés
                </Button>
              )}
            </div>
            {["x", "y", "z"].map((axis) => (
              <ExpressionEditor
                key={axis}
                axis={axis.toUpperCase()}
                expression={vector[axis] || createExpression()}
                settings={settings}
                onChange={(expression) => updateVector(index, axis, expression)}
              />
            ))}
          </Card.Body>
        </Card>
      ))}
    </div>
  );
}

function RuleNodeEditor({ rule, settings, onChange, kind, path = kind }) {
  const branchEditor = (field, title) => {
    const branch = rule[field];
    return <div className="border rounded p-2 mb-2" role="group" aria-label={`${path}: ${title}`}>
      {Array.isArray(branch) ? <>
        <VectorListEditor title={title} vectors={branch} settings={settings} kind={kind}
          onChange={(next) => onChange({ ...rule, [field]: next })} />
        <Button size="sm" variant="outline-primary" aria-label={`${path}: ${title}: Elágazás hozzáadása`}
          onClick={() => onChange({ ...rule, [field]: { condition: createCondition(), whenTrue: branch, whenFalse: [createVector()] } })}>
          + Elágazás
        </Button>
      </> : <>
        <strong>{title}</strong>
        <RuleNodeEditor rule={branch} settings={settings} kind={kind} path={`${path}: ${title}`}
          onChange={(next) => onChange({ ...rule, [field]: next })} />
      </>}
    </div>;
  };
  if (!rule.condition) return <>
    <VectorListEditor title="" vectors={rule.whenTrue} settings={settings} kind={kind}
      onChange={(whenTrue) => onChange({ ...rule, whenTrue })} />
    <Button size="sm" variant="outline-primary" aria-label={`${path}: Elágazás hozzáadása`}
      onClick={() => onChange({ ...rule, condition: createCondition(), whenFalse: [createVector()] })}>
      + Elágazás
    </Button>
  </>;
  const retain = (branch) => onChange(Array.isArray(branch)
    ? { condition: null, whenTrue: branch, whenFalse: [] } : branch);
  return <div className="mt-2">
    <Row className="g-2 mb-2 align-items-center">
      <Col xs={12}>
        <ExpressionEditor axis="Bal oldal" expression={rule.condition.left} settings={settings}
          onChange={(left) => onChange({ ...rule, condition: { ...rule.condition, left } })} />
      </Col>
      <Col xs="auto">
        <Form.Select size="sm" value={rule.condition.operator} aria-label={`${path}: Összehasonlítás`}
          onChange={(event) => onChange({ ...rule, condition: { ...rule.condition, operator: event.target.value } })}>
          {QUANTITY_RELATIONS.map((operator) => <option key={operator} value={operator}>{operator}</option>)}
        </Form.Select>
      </Col>
      <Col xs={12}>
        <ExpressionEditor axis="Jobb oldal" expression={rule.condition.right} settings={settings}
          onChange={(right) => onChange({ ...rule, condition: { ...rule.condition, right } })} />
      </Col>
    </Row>
    <div className="d-flex flex-wrap gap-2 mb-2">
      <Button size="sm" variant="outline-danger" onClick={() => retain(rule.whenTrue)}>Elágazás törlése, igaz ág megtartása</Button>
      <Button size="sm" variant="outline-danger" onClick={() => retain(rule.whenFalse)}>Elágazás törlése, hamis ág megtartása</Button>
    </div>
    {branchEditor("whenTrue", "Ha igaz")}
    {branchEditor("whenFalse", "Ha hamis")}
  </div>;
}

function readRule(source) {
  try { return { source, rule: parsePositionRule(source), error: "" }; }
  catch { return { source, rule: null, error: "Hibás vagy befejezetlen szabály. Javítsd a Szabály mezőben." }; }
}

export default function PositionRuleBuilder({ settings, value, onChange, onValidityChange, hideTitle = false, kind = "Pozíció" }) {
  const [editor, setEditor] = useState(() => readRule(value || ""));
  useEffect(() => {
    if ((value || "") !== editor.source) setEditor(readRule(value || ""));
  }, [value, editor.source]);
  const errors = useMemo(() => editor.rule ? validatePositionRule(editor.rule) : [editor.error], [editor]);
  useEffect(() => {
    onValidityChange?.(!errors.some((error) => !error.startsWith("Az igaz és hamis ág azonos")));
  }, [errors, onValidityChange]);
  const updateRule = (rule) => {
    const source = compilePositionRule(rule);
    setEditor({ source, rule, error: "" });
    onChange(source);
  };
  return <Card className="mb-3">
    <Card.Header className="d-flex justify-content-between align-items-center">
      {!hideTitle && <strong>{kind}</strong>}
      <Button size="sm" variant="outline-danger" onClick={() => updateRule(createPositionRule())}>Szabály törlése</Button>
    </Card.Header>
    <Card.Body>
      {editor.rule && <RuleNodeEditor rule={editor.rule} settings={settings} kind={kind} onChange={updateRule} />}
      <Form.Label className="mt-3" htmlFor={`${kind}-rule`}>Szabály</Form.Label>
      <Form.Control id={`${kind}-rule`} as="textarea" rows={3} value={editor.source}
        placeholder="[[0,0,0]]" aria-label={`${kind}: Szabály`}
        onChange={(event) => {
          const source = event.target.value;
          if (!source.trim()) { updateRule(createPositionRule()); return; }
          setEditor(readRule(source));
          onChange(source);
        }} />
      {errors.map((error) => <div role="status" className="text-warning small mt-1" key={error}>{error}</div>)}
    </Card.Body>
  </Card>;
}

